import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { API, formatDate } from '../utils';

const buildDefaults = (property) => {
  const address = property?.address || 'your property';
  const tenant = property?.tenant_name?.split(' ')[0] || 'there';
  const endDate = property?.tenancy_end_date ? formatDate(property.tenancy_end_date) : 'soon';
  const subject = `Your tenancy at ${address} ends on ${endDate}`;
  const body =
`Hi ${tenant},

Just a friendly reminder that your tenancy at ${address} ends on ${endDate}.

I wanted to check in early to see whether you'd like to renew for another term or move on at the end of the current tenancy. Either way, I'd love to hear your plans so we can get the paperwork sorted with plenty of time to spare.

Could you let me know your thoughts in the next week or so? Happy to jump on a call if it's easier.

Thanks,`;
  return { subject, body };
};

export default function EmailRenewalDialog({ open, onOpenChange, property }) {
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  // Re-seed defaults whenever the dialog is opened for a new property.
  useEffect(() => {
    if (!open || !property) return;
    const { subject: s, body: b } = buildDefaults(property);
    setToEmail(property.tenant_email || '');
    setSubject(s);
    setBody(b);
  }, [open, property]);

  const handleSend = async () => {
    if (!toEmail.trim()) {
      toast.error('Tenant email is required');
      return;
    }
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body cannot be empty');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/properties/${property.id}/email-tenant-renewal`,
        { to_email: toEmail.trim(), subject: subject.trim(), body },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Renewal email sent to ${toEmail.trim()}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!sending) onOpenChange(o); }}>
      <DialogContent className="ai-review-dialog" data-testid="email-renewal-dialog">
        <DialogHeader>
          <DialogTitle>
            <Mail size={18} style={{ display: 'inline-block', marginRight: 8, verticalAlign: '-3px' }} />
            Email tenant about renewal
          </DialogTitle>
          <DialogDescription>
            Send a polite renewal-nudge email to your tenant via Resend. Replies go straight to your registered email.
          </DialogDescription>
        </DialogHeader>
        <p className="ai-review-hint" style={{ marginBottom: 14 }}>
          Sent via Resend. Replies go directly to your registered email — the tenant just hits Reply.
        </p>
        <div className="form-group">
          <Label htmlFor="email-renewal-to">To</Label>
          <Input
            id="email-renewal-to"
            type="email"
            placeholder="tenant@example.com"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            data-testid="email-renewal-to-input"
          />
          {!property?.tenant_email && (
            <p className="form-hint">
              No tenant email is saved for this property yet. Type one in here, then add it to the tenant record via <strong>Edit Tenant Details</strong> so you don't have to retype next time.
            </p>
          )}
        </div>
        <div className="form-group">
          <Label htmlFor="email-renewal-subject">Subject</Label>
          <Input
            id="email-renewal-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="email-renewal-subject-input"
          />
        </div>
        <div className="form-group">
          <Label htmlFor="email-renewal-body">Message</Label>
          <textarea
            id="email-renewal-body"
            className="email-renewal-textarea"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            data-testid="email-renewal-body-input"
          />
        </div>
        <div className="ai-review-actions">
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            className="btn-secondary"
            disabled={sending}
            data-testid="email-renewal-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            className="btn-primary"
            disabled={sending || !toEmail.trim()}
            data-testid="email-renewal-send"
          >
            {sending ? 'Sending…' : 'Send email'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
